import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { handleResponseError } from '@/utils/toastUtils'
import { isInvalidPublicCapability } from '@/composables/usePublicContext'

export const attachmentFiles = (value) => (Array.isArray(value) ? value : value ? [value] : [])

/** Coordinate presentation without interpreting domain IDs, payloads or receipts. */
export function useAttachmentUpload(props, emit) {
  const view = ref('form')
  const files = ref([])
  const fileName = ref('')
  const metadata = reactive({})
  const valid = ref(false)
  const loading = ref(false)
  const loadFailed = ref(false)
  const sessionError = ref(false)
  const sessionInvalid = ref(false)
  const missingUpload = ref(false)
  const session = ref(null)
  const snapshot = ref(null)
  const attachments = ref([])
  const warnings = ref([])
  const changed = ref(false)
  const addedIds = ref([])
  let generation = 0
  let active = true
  let sessionMetadata = ''
  const capabilities = computed(() => props.adapter.capabilities)
  const localSupported = computed(() => capabilities.value.supportsLocal !== false)
  const remaining = computed(() =>
    capabilities.value.maxTotal == null
      ? Infinity
      : Math.max(0, capabilities.value.maxTotal - attachments.value.length)
  )
  const uploadBlocked = computed(() => remaining.value === 0)
  const current = (id) => active && id === generation
  const result = (confirmed) => ({
    confirmed,
    changed: changed.value,
    snapshot: snapshot.value,
    addedIds: [...addedIds.value],
    warnings: [...warnings.value]
  })
  function setBusy(value) {
    loading.value = value
    emit('busyChange', value)
  }
  function acceptSnapshot(value) {
    if (value == null) return
    snapshot.value = value
    emit('update:snapshot', value)
  }
  function applyOutcome(outcome = {}) {
    acceptSnapshot(outcome.snapshot)
    warnings.value = [...new Set([...warnings.value, ...(outcome.warnings ?? [])])]
    addedIds.value = [...new Set([...addedIds.value, ...(outcome.addedIds ?? [])])]
  }
  async function refresh(adapter, id, mutation = false) {
    try {
      const state = await adapter.readCurrent()
      if (!current(id)) return false
      acceptSnapshot(state.snapshot)
      attachments.value = state.attachments ?? []
      loadFailed.value = false
      return true
    } catch (error) {
      if (!current(id)) return false
      if (mutation) {
        warnings.value = [...new Set([...warnings.value, 'ATTACHMENT_UPLOAD.REFRESH_FAILED'])]
      } else {
        loadFailed.value = true
        handleResponseError(error)
      }
      return false
    }
  }
  async function createSession(adapter, id) {
    sessionError.value = false
    sessionInvalid.value = false
    missingUpload.value = false
    try {
      const created = await adapter.createQrSession({ metadata: { ...metadata } })
      if (!current(id)) return
      if (!created?.token || !created?.url) throw new Error('Invalid upload session')
      session.value = created
      sessionMetadata = JSON.stringify(metadata)
    } catch (error) {
      if (current(id)) {
        session.value = null
        sessionError.value = true
        handleResponseError(error)
      }
    }
  }
  async function initialize() {
    const id = ++generation
    const adapter = props.adapter
    view.value = props.initialView === 'qr' && adapter.capabilities.supportsQr ? 'qr' : 'form'
    files.value = []
    fileName.value = ''
    valid.value = false
    session.value = null
    sessionError.value = false
    sessionInvalid.value = false
    missingUpload.value = false
    snapshot.value = null
    attachments.value = []
    warnings.value = []
    changed.value = false
    addedIds.value = []
    for (const key of Object.keys(metadata)) delete metadata[key]
    Object.assign(metadata, adapter.metadata ?? {})
    setBusy(true)
    if (await refresh(adapter, id)) {
      if (view.value === 'qr') await createSession(adapter, id)
    }
    if (current(id)) setBusy(false)
  }
  async function openQr(retry = false) {
    if (loading.value || !capabilities.value.supportsQr || loadFailed.value) return
    view.value = 'qr'
    missingUpload.value = false
    if (!retry && session.value && sessionMetadata === JSON.stringify(metadata)) return
    session.value = null
    const id = generation
    setBusy(true)
    if (await refresh(props.adapter, id)) await createSession(props.adapter, id)
    if (current(id)) setBusy(false)
  }
  async function reloadCurrent() {
    if (loading.value) return
    const id = generation
    const adapter = props.adapter
    setBusy(true)
    const loaded = await refresh(adapter, id, changed.value)
    if (!current(id)) return
    if (loaded) {
      warnings.value = warnings.value.filter((key) => key !== 'ATTACHMENT_UPLOAD.REFRESH_FAILED')
      if (view.value === 'qr' && !session.value) await createSession(adapter, id)
    }
    if (current(id)) setBusy(false)
  }
  async function cancel() {
    if (loading.value) return
    const id = generation
    setBusy(true)
    const before = attachments.value.map((item) => item.id).join('|')
    await refresh(props.adapter, id, true)
    if (!current(id)) return
    if (before !== attachments.value.map((item) => item.id).join('|')) changed.value = true
    setBusy(false)
    if (props.embedded && localSupported.value) {
      view.value = 'form'
      files.value = []
      fileName.value = ''
      valid.value = false
    }
    emit('cancelled', result(false))
  }
  function back() {
    if (loading.value) return
    if (!localSupported.value) return cancel()
    view.value = 'form'
    missingUpload.value = false
  }
  async function upload() {
    const selected = attachmentFiles(files.value)
    if (
      loading.value ||
      loadFailed.value ||
      !valid.value ||
      !localSupported.value ||
      !selected.length ||
      selected.length > remaining.value ||
      (!capabilities.value.multiple && selected.length !== 1) ||
      (capabilities.value.requiresName && !fileName.value.trim())
    )
      return
    const id = generation
    const adapter = props.adapter
    warnings.value = []
    setBusy(true)
    try {
      const outcome = await adapter.uploadLocal({
        files: selected,
        fileName: fileName.value.trim(),
        metadata: { ...metadata }
      })
      if (!current(id)) return
      changed.value = true
      applyOutcome(outcome)
      await refresh(adapter, id, true)
      if (!current(id)) return
      if (outcome?.partial) {
        files.value = outcome.failedFiles ?? selected
        warnings.value = [...new Set([...warnings.value, 'ATTACHMENT_UPLOAD.PARTIAL'])]
      } else {
        files.value = []
        emit('confirmed', result(true))
      }
    } catch (error) {
      if (current(id)) handleResponseError(error)
    } finally {
      if (current(id)) setBusy(false)
    }
  }
  async function confirmQr() {
    if (loading.value || !session.value) return
    const id = generation
    const adapter = props.adapter
    setBusy(true)
    missingUpload.value = false
    try {
      const verification = await adapter.verifyQrUpload({ session: session.value })
      if (!current(id)) return
      if (verification?.completed !== true) {
        missingUpload.value = true
        return
      }
      changed.value = true
      applyOutcome(verification)
      if (adapter.finalizeQrSession) {
        const finalized = await adapter.finalizeQrSession({ session: session.value, verification })
        if (!current(id)) return
        applyOutcome(finalized)
      }
      await refresh(adapter, id, true)
      if (!current(id)) return
      session.value = null
      files.value = []
      if (localSupported.value) view.value = 'form'
      emit('confirmed', result(true))
    } catch (error) {
      if (current(id)) {
        if (isInvalidPublicCapability(error)) {
          session.value = null
          sessionError.value = true
          sessionInvalid.value = true
        }
        handleResponseError(error)
      }
    } finally {
      if (current(id)) setBusy(false)
    }
  }
  async function remove(attachment) {
    if (loading.value || !capabilities.value.supportsDelete) return
    const id = generation
    const adapter = props.adapter
    setBusy(true)
    try {
      const outcome = await adapter.removeExisting(attachment)
      if (!current(id)) return
      changed.value = true
      applyOutcome(outcome)
      await refresh(adapter, id, true)
    } catch (error) {
      if (current(id)) handleResponseError(error)
    } finally {
      if (current(id)) setBusy(false)
    }
  }
  watch(() => props.adapter, initialize, { immediate: true })
  onBeforeUnmount(() => {
    active = false
    generation++
  })
  return {
    view,
    files,
    fileName,
    metadata,
    valid,
    loading,
    loadFailed,
    sessionError,
    sessionInvalid,
    missingUpload,
    session,
    attachments,
    warnings,
    capabilities,
    localSupported,
    remaining,
    uploadBlocked,
    initialize,
    reloadCurrent,
    openQr,
    back,
    upload,
    confirmQr,
    cancel,
    remove
  }
}
